import { useState, useEffect, useCallback } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { JobStatus, JobStage, TravelTimeRate, TravelToJob, WarehouseTransitLeg } from '../types/admin';

export interface AssignedCrewMember {
  userId?: string;
  displayName: string;
  isCrewLead: boolean;
  isDriver: boolean;
  isContractor: boolean;
}

export interface AssignedTruck {
  truckId: string;
  truckName: string;
}

export interface Job {
  id: string;
  orgId: string;
  quoteNumber?: string;
  customerCompanyName: string;
  assignedCrew?: AssignedCrewMember[];
  assignedTrucks?: AssignedTruck[];
  jobStatus: JobStatus;
  jobStage?: JobStage;
  leadStatus?: string;
  quoteType?: 'commercial' | 'residential';
  packingDate?: string | null;
  moveType?: 'local' | 'intrastate' | 'interstate';
  movingDate?: {
    preferredDate?: string;
    deadline?: string;
    timing?: string;
    shiftType?: string;
    shiftLengthHours?: number;
  };
  addresses?: {
    origin?: { address: string; placeId: string; floor?: string; officeNumber?: string; apt?: string };
    internalMove?: boolean;
    destination?: { address: string; placeId: string; floor?: string; officeNumber?: string; apt?: string };
    transit?: { distanceMeters: number; durationSeconds: number; calculatedAt: string };
  };
  warehouseTransit?: Record<string, { toOrigin?: WarehouseTransitLeg; toDestination?: WarehouseTransitLeg }>;
  travelToJob?: TravelToJob;
  travelTimeRate?: TravelTimeRate;
  estimatedMoveDateRange?: string;
  pointOfContact?: {
    name?: string;
    phone?: string;
    email?: string;
    notes?: string;
  };
  numberOfEmployees?: number;
  scopeOfServices?: Array<{
    serviceId: string;
    serviceName: string;
    party: 'Customer' | 'Mover';
    order: number;
    enabled: boolean;
    isCustom?: boolean;
    note?: string;
  }>;
  casegoodsInventory?: Array<{
    itemId: string;
    itemName: string;
    quantity: number;
  }>;
  modularWorkstations?: {
    totalWorkstations: number;
    moverDisassembling: boolean;
    moverReassembling: boolean;
    workstationsSampled: number;
    samples: Array<{ itemId: string; itemName: string; quantitySampled: number }>;
  };
  notes?: string;
  photos?: Array<{ id: string; fileName: string; url: string; createdAt: string }>;
  logisticsSummary?: {
    crewSize?: number;
    truckQuantity?: number;
    estimatedHours?: number;
    method?: 'Shuttle' | 'Load & Go' | 'Internal';
    driverQuantity?: number;
    travelTimeToJobHours?: number;
    finalTransitTimeHours?: number;
    totalBillableHours?: number;
    totalHourlyRate?: number;
    finalCostEstimate?: number;
    isFlatRate?: boolean;
  };
  moveCompletionDate?: string;
  customerSignOff?: {
    status: 'captured' | 'waived';
    signerName?: string;
    signatureUrl?: string;
    waiveReason?: string;
    capturedBy: string;
    capturedAt: string;
  };
  actualJobTimes?: {
    travelTimeToJobSeconds: number | null;
    loadTimeSeconds: number | null;
    transitTimeSeconds: number | null;
    unloadTimeSeconds: number | null;
    totalJobDurationSeconds: number | null;
    travelTimeToWarehouseSeconds: number | null;
    totalBillableTimeSeconds: number | null;
    timestamps: {
      truckCheckedOut?: string | null;
      arrivedAtOrigin?: string | null;
      loadingComplete?: string | null;
      arrivedAtDestination?: string | null;
      unloadingComplete?: string | null;
      truckCheckedIn?: string | null;
    };
  };
  residentialRooms?: Array<{
    id: string;
    roomId: string;
    roomName: string;
    profileLevel: string;
    items: Array<{ itemId: string; itemName: string; quantity: number }>;
  }>;
  issues?: JobIssue[];
  paymentSummary?: {
    paymentStatus: 'no_schedule' | 'unpaid' | 'partially_paid' | 'fully_paid' | 'overpaid';
    totalAmountCents: number;
    collectedAmountCents: number;
    outstandingAmountCents: number;
    paymentScheduleType?: string;
    nextPaymentDue?: {
      installmentId: string;
      label: string;
      amountCents: number;
      dueDate?: string;
    };
    subtotalAmountCents?: number;
    taxState?: string;
    laborTaxRate?: number;
    taxAmountCents?: number;
    requiresManualTaxReview?: boolean;
  };
  paymentSchedule?: {
    type: string;
    installments: Array<{
      id: string;
      label: string;
      type: 'deposit' | 'installment' | 'final';
      amountCents: number;
      percentage?: number;
      dueDate?: string;
      dueTrigger?: string;
      status: string;
    }>;
  };
  createdAt: string;
  updatedAt: string;
}

export interface JobIssue {
  id: string;
  category: string;
  issue: string;
  severity: string;
  responsibility: string;
  notes: string;
  photos?: Array<{ id: string; fileName: string; url: string; createdAt: string }>;
  loggedBy: { userId: string; displayName: string };
  timestamp: string;
}

export type JobSortField = 'movingDate' | 'customerCompanyName' | 'jobStatus';
export type SortDir = 'asc' | 'desc';

export interface JobStats {
  scheduled: number;
  in_progress: number;
  completed: number;
  on_hold: number;
  cancelled: number;
  total: number;
}

export interface UseJobsParams {
  search?: string;
  sortBy?: JobSortField;
  sortDir?: SortDir;
  status?: JobStatus;
  moveLeadUserId?: string;
}

interface ListResponse {
  success: boolean;
  data: Job[];
  hasMore: boolean;
  nextCursor?: string;
  stats: JobStats;
}

const PAGE_SIZE = 20;

function buildJobQuery(params: UseJobsParams, cursor?: string): string {
  const qs = new URLSearchParams();
  qs.set('limit', String(PAGE_SIZE));
  if (cursor) qs.set('cursor', cursor);
  if (params.search) qs.set('search', params.search);
  if (params.sortBy) qs.set('sortBy', params.sortBy);
  if (params.sortDir) qs.set('sortDir', params.sortDir);
  if (params.status) qs.set('status', params.status);
  if (params.moveLeadUserId) qs.set('moveLeadUserId', params.moveLeadUserId);
  return qs.toString();
}

/**
 * Fetches and manages a paginated list of jobs for an org.
 * Supports search, sort, filtering by status/mover, and infinite scroll.
 * Uses React Query for caching and deduplication.
 */
export function useJobs(orgId: string | null, params: UseJobsParams = {}) {
  const { search, sortBy = 'movingDate', sortDir = 'asc', status, moveLeadUserId } = params;
  const resolvedParams = { search, sortBy, sortDir, status, moveLeadUserId };

  const {
    data,
    isLoading,
    isFetchingNextPage,
    error: queryError,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['jobs', orgId, resolvedParams],
    queryFn: async ({ pageParam }) => {
      const qs = buildJobQuery(resolvedParams, pageParam as string | undefined);
      return api.get<ListResponse>(`/jobs/${orgId}?${qs}`);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.nextCursor : undefined,
    enabled: !!orgId,
  });

  const jobs = data?.pages.flatMap(p => p.data) ?? [];
  const stats = data?.pages[0]?.stats ?? { scheduled: 0, in_progress: 0, completed: 0, on_hold: 0, cancelled: 0, total: 0 };

  const fetchMore = useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    jobs,
    loading: isLoading,
    loadingMore: isFetchingNextPage,
    error: queryError ? (queryError instanceof Error ? queryError.message : 'Failed to load jobs') : null,
    hasMore: !!hasNextPage,
    stats,
    fetchMore,
    refetch,
  };
}

export function useJob(orgId: string | null, quoteId: string | null) {
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchJob = useCallback(async () => {
    if (!orgId || !quoteId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ success: boolean; data: Job }>(`/jobs/${orgId}/${quoteId}`);
      setJob(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load job');
    } finally {
      setLoading(false);
    }
  }, [orgId, quoteId]);

  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  const updateJobStatus = useCallback(
    async (jobStatus: JobStatus, moveCompletionDate?: string) => {
      if (!orgId || !quoteId) throw new Error('No job loaded');
      await api.patch<{ success: boolean; data: { jobStatus: JobStatus; moveCompletionDate?: string } }>(
        `/jobs/${orgId}/${quoteId}/status`,
        { jobStatus, ...(moveCompletionDate ? { moveCompletionDate } : {}) },
      );
      setJob(prev => prev ? { ...prev, jobStatus, ...(moveCompletionDate ? { moveCompletionDate } : {}) } : prev);
    },
    [orgId, quoteId],
  );

  const updateJobStage = useCallback(
    async (action: 'advance' | 'revert') => {
      if (!orgId || !quoteId) throw new Error('No job loaded');
      const res = await api.patch<{ success: boolean; data: { jobStage: JobStage; jobStatus: JobStatus; moveCompletionDate?: string } }>(
        `/jobs/${orgId}/${quoteId}/stage`,
        { action },
      );
      setJob(prev => prev ? { ...prev, ...res.data } : prev);
    },
    [orgId, quoteId],
  );

  const updateJobCrew = useCallback(
    async (assignedCrew: AssignedCrewMember[]) => {
      if (!orgId || !quoteId) throw new Error('No job loaded');
      await api.patch<{ success: boolean; data: { assignedCrew: AssignedCrewMember[] } }>(
        `/jobs/${orgId}/${quoteId}/crew`,
        { assignedCrew },
      );
      setJob(prev => prev ? { ...prev, assignedCrew } : prev);
    },
    [orgId, quoteId],
  );

  const updateJobTrucks = useCallback(
    async (assignedTrucks: AssignedTruck[]) => {
      if (!orgId || !quoteId) throw new Error('No job loaded');
      await api.patch<{ success: boolean; data: { assignedTrucks: AssignedTruck[] } }>(
        `/jobs/${orgId}/${quoteId}/trucks`,
        { assignedTrucks },
      );
      setJob(prev => prev ? { ...prev, assignedTrucks } : prev);
    },
    [orgId, quoteId],
  );

  const updateJobDates = useCallback(
    async (movingDate: { preferredDate?: string; deadline?: string }) => {
      if (!orgId || !quoteId) throw new Error('No job loaded');
      await api.patch(`/quotes/${orgId}/${quoteId}`, { movingDate });
      setJob(prev => prev ? { ...prev, movingDate: { ...prev.movingDate, ...movingDate } } : prev);
    },
    [orgId, quoteId],
  );

  const updateJobContact = useCallback(
    async (pointOfContact: { name?: string; phone?: string; email?: string }) => {
      if (!orgId || !quoteId) throw new Error('No job loaded');
      await api.patch(`/quotes/${orgId}/${quoteId}`, { pointOfContact });
      setJob(prev => prev ? { ...prev, pointOfContact: { ...prev.pointOfContact, ...pointOfContact } } : prev);
    },
    [orgId, quoteId],
  );

  const updateJobDatesLogistics = useCallback(
    async (patch: Record<string, unknown>) => {
      if (!orgId || !quoteId) throw new Error('No job loaded');
      const res = await api.patch<{ success: boolean; data: Job }>(`/quotes/${orgId}/${quoteId}`, patch);
      setJob(res.data);
    },
    [orgId, quoteId],
  );

  const calculateTransit = useCallback(
    async (originPlaceId: string, destinationPlaceId: string) => {
      if (!orgId || !quoteId) throw new Error('No job loaded');
      const res = await api.post<{ success: boolean; data: { distanceMeters: number; durationSeconds: number; calculatedAt: string } }>(
        `/quotes/${orgId}/${quoteId}/calculate-transit`,
        { originPlaceId, destinationPlaceId },
      );
      setJob(prev => prev ? { ...prev, addresses: { ...(prev.addresses ?? {}), transit: res.data } } : prev);
      return res.data;
    },
    [orgId, quoteId],
  );

  const calculateWarehouseTransits = useCallback(
    async (
      warehouseList: Array<{ id: string; placeId: string }>,
      originPlaceId: string,
      destinationPlaceId: string,
    ) => {
      if (!orgId || !quoteId) throw new Error('No job loaded');
      const res = await api.post<{
        success: boolean;
        data: Record<string, { toOrigin?: WarehouseTransitLeg; toDestination?: WarehouseTransitLeg }>;
      }>(`/quotes/${orgId}/${quoteId}/calculate-warehouse-transits`, {
        warehouses: warehouseList,
        originPlaceId,
        destinationPlaceId,
      });
      setJob(prev => prev ? { ...prev, warehouseTransit: res.data } : prev);
      return res.data;
    },
    [orgId, quoteId],
  );

  const logJobIssue = useCallback(
    async (issueData: {
      category: string;
      issue: string;
      severity: string;
      responsibility: string;
      notes: string;
      photos?: Array<{ imageData: string; fileName: string }>;
    }): Promise<JobIssue> => {
      if (!orgId || !quoteId) throw new Error('No job loaded');
      const res = await api.post<{ success: boolean; data: JobIssue }>(
        `/jobs/${orgId}/${quoteId}/issues`,
        issueData,
      );
      setJob(prev => prev ? { ...prev, issues: [...(prev.issues ?? []), res.data] } : prev);
      return res.data;
    },
    [orgId, quoteId],
  );

  const checkOutJobTruck = useCallback(
    async (truckId: string) => {
      if (!orgId || !quoteId) throw new Error('No job loaded');
      const res = await api.patch<{ success: boolean; data: { truckId: string; checkedOut: boolean; stageAdvanced: boolean; jobStage?: string } }>(
        `/jobs/${orgId}/${quoteId}/truck-checkout`,
        { truckId },
      );
      if (res.data.stageAdvanced && res.data.jobStage) {
        setJob(prev => prev ? { ...prev, jobStage: res.data.jobStage as JobStage } : prev);
      }
      return res.data;
    },
    [orgId, quoteId],
  );

  const checkInJobTruck = useCallback(
    async (truckId: string, warehouseId: string) => {
      if (!orgId || !quoteId) throw new Error('No job loaded');
      const res = await api.patch<{ success: boolean; data: { truckId: string; checkedIn: boolean; stageAdvanced: boolean; jobStage?: string; jobStatus?: string } }>(
        `/jobs/${orgId}/${quoteId}/truck-checkin`,
        { truckId, warehouseId },
      );
      if (res.data.stageAdvanced) {
        setJob(prev => prev ? { ...prev, jobStage: res.data.jobStage as JobStage, jobStatus: res.data.jobStatus as JobStatus } : prev);
      }
      return res.data;
    },
    [orgId, quoteId],
  );

  return { job, loading, error, updateJobStatus, updateJobStage, updateJobCrew, updateJobTrucks, updateJobDates, updateJobDatesLogistics, updateJobContact, calculateTransit, calculateWarehouseTransits, logJobIssue, checkOutJobTruck, checkInJobTruck, refetch: fetchJob };
}
